import { action, computed } from '@ember/object';
import { inject as service } from '@ember/service';
import { readOnly, or } from '@ember/object/computed';
import Controller from '@ember/controller';
import colorLabels from 'cio/utils/color-labels';
import actionInfo from 'cio/utils/action-info';
import { Measure, MEASURE_RATE_TITLES } from 'cio/utils/metrics/common';
import { pluralize } from 'ember-inflector';
import moment from 'moment-timezone';
import { capitalize } from '@ember/string';
import ApiService from 'cio/services/api';
import ApiErrorsService from 'cio/services/api-errors';
import EnvironmentService from 'cio/services/environment';
import { SplitMetric } from 'cio/utils/metrics/models/split-metric';
import ActionModel, { ActionType } from 'cio/models/action';
import { dropTask } from 'ember-concurrency';
import { MetricDenominator } from 'cio/models/environment';
import { isDeliveryMeasureEnabledForActionType } from 'cio/utils/metrics/measure';
import AuthService from 'cio/services/auth';
import RouterService from '@ember/routing/router-service';

function statSeries(
  splitMetrics: SplitMetric[],
  key: MetricDenominator | 'clicked' | 'converted' | 'opened',
  isRatio: boolean,
) {
  return splitMetrics
    .filter((splitMetric) => splitMetric[key])
    .map((splitMetric, index) => {
      let { label, hex_dark_name } = colorLabels[index];

      let data = isRatio ? splitMetric[key]!.cumulativeAverageRates : splitMetric[key]!.totals;

      if (isRatio) {
        data = data.map((point) => ({ ...point, y: point.y != null ? point.y * 100 : null }));
      }

      return {
        name: label,
        type: isRatio ? 'line' : 'column',
        data,
        colorIndex: hex_dark_name,
      };
    });
}

export default class TestController extends Controller {
  @service api!: ApiService;
  @service apiErrors!: ApiErrorsService;
  @service environment!: EnvironmentService;
  @service auth!: AuthService;
  @service router!: RouterService;

  @or('pauseTest.isRunning', 'resumeTest.isRunning') isSaving!: boolean;

  declare actionModel: ActionModel;
  declare splitMetrics: SplitMetric[];
  declare selectedWinner: ActionModel | null;
  declare selectedStat: Record<string, any> | null;

  isDeleting = false;

  @readOnly('splitMetrics.firstObject') originalMetric!: SplitMetric;

  originalBadge = colorLabels[0];

  @computed('environment.current.metricDenominator')
  get denominator() {
    return this.environment.current.metricDenominator;
  }

  @computed('actionModel.actions.@each.type')
  get availableMeasures() {
    let actionMeasures = this.actionModel.actions
      .reduce((measures, action) => [...measures, ...(actionInfo(action.get('type')).deliveryMetrics ?? [])], [])
      .uniq();
    let splitMeasures = (
      ['opened', 'clicked', 'converted'] as Extract<Measure, 'clicked' | 'converted' | 'opened'>[]
    ).filter((measure) =>
      isDeliveryMeasureEnabledForActionType(measure, (this.splitMetrics[0]?.actionType ?? '') as ActionType),
    );

    return splitMeasures
      .filter((measure) => actionMeasures.includes(measure))
      .map((measure) => ({
        key: measure,
        title: MEASURE_RATE_TITLES[measure],
      }));
  }

  @computed('actionModel.isSplitInactive', 'splitMetrics.@each.sent')
  get showResults() {
    if (this.actionModel.get('isSplitInactive')) {
      return false;
    }

    return (this.splitMetrics ?? []).any((metric) => metric.sent.total > 0);
  }

  @computed('splitMetrics.[]')
  get variationSplitMetricItems() {
    return this.splitMetrics
      .map((metric, index) => {
        return {
          badge: colorLabels[index],
          metric,
        };
      })
      .slice(1);
  }

  @computed('availableMeasures', 'splitMetrics.[]', 'denominator')
  get stats(): Record<string, any> {
    let measures = [
      {
        key: this.denominator,
        title: capitalize(this.denominator),
      },
      ...this.availableMeasures,
    ];

    return measures.map(({ key, title }, i) => {
      let isRatio = i !== 0;
      return {
        label: isRatio ? pluralize(title) : title,
        value: key,
        series: statSeries(this.splitMetrics, key, isRatio),
      };
    });
  }

  @computed('splitMetrics.firstObject.sent')
  get timeRange() {
    let [firstMetric] = this.splitMetrics;

    let end = moment.tz('Etc/GMT+4').startOf('day');
    let start = moment(end).subtract(firstMetric.sent.totals.length - 1, 'days');
    return {
      start,
      end,
    };
  }

  get disabled() {
    let hasReadOnlyPermission = false;
    if (this.actionModel.campaign.isApiTriggered) {
      hasReadOnlyPermission = !this.auth.hasPermission('journeys/broadcasts/edit');
    } else {
      hasReadOnlyPermission = !this.auth.hasPermission('journeys/campaigns/edit');
    }

    return this.actionModel.campaign.isLocked || this.isSaving || hasReadOnlyPermission;
  }

  pauseTest = dropTask(async () => {
    let promise = this.actionModel.pauseTest();

    this.apiErrors.attach(promise);

    try {
      await promise;
    } catch (e) {
      this.actionModel.rollbackAttributes();
    }
  });

  resumeTest = dropTask(async () => {
    let promise = this.actionModel.startTest();

    this.apiErrors.attach(promise);

    try {
      await promise;
    } catch (e) {
      this.actionModel.rollbackAttributes();
    }
  });

  @action
  redirectToWorkflow() {
    this.router.transitionTo(
      // Use any because typescript doesn't think `isApiTriggered` is a method
      // on ActionModel. Could be an old backward compat check
      (this.actionModel as any).isApiTriggered
        ? 'broadcasts.broadcast.overview.workflow'
        : 'campaigns.campaign.overview.workflow',
    );
  }

  @action
  deleteTest() {
    this.setProperties({
      selectedWinner: this.actionModel.splitActions.firstObject,
      isDeleting: true,
    });
  }

  @action
  selectWinner(item: ActionModel) {
    this.setProperties({
      selectedWinner: item,
      isDeleting: false,
    });
  }

  @action
  selectStatByValue(value: MetricDenominator) {
    let stat = this.stats.findBy('value', value) ?? this.stats[0];

    this.set('selectedStat', stat);
  }
}
